import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils'
import { useDataTable } from '@/hooks/useDataTable-hook'
import { GetFormattedProductData } from '@/models/ProductListModel'
import { ProductListColConfig } from '@/services/MockData/Products/ProductListColConfig'
import { getProductCsvFile, getProductList } from '@/services/ProductsService/product'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAppSelector } from '@/store'

export const useProductList = (options = {}) => {
  // Extract productIds from salesTarget state
  const salesTargetData = useAppSelector(state => state.salesTarget)
  const { isReviewPage = false } = options
  
  const extractProductIds = () => {
    if (!salesTargetData) {
      return []
    }
    
    let includedIds = []
    let excludedIds = []
    
    if (isReviewPage) {
      if (!salesTargetData.ruleSet || !salesTargetData.ruleSet.rules || !salesTargetData.ruleSet.rules[0]) {
        return []
      }
      
      if(salesTargetData.tabWiseData?.activeTab === "child") {
        includedIds = salesTargetData.tabWiseData?.child?.ruleSet?.rules?.[0]?.includedProductIds || []
        excludedIds = salesTargetData.tabWiseData?.child?.ruleSet?.rules?.[0]?.excludedProductIds || []
      } else {
        includedIds = salesTargetData.ruleSet.rules[0].includedProductIds || []
        excludedIds = salesTargetData.ruleSet.rules[0].excludedProductIds || []
      }
    } else {
      if (!salesTargetData.tabWiseData) {
        return []
      }
      if(salesTargetData.tabWiseData?.activeTab === "child") {
        includedIds = salesTargetData.tabWiseData?.child?.ruleSet?.rules?.[0]?.includedProductIds || []
        excludedIds = salesTargetData.tabWiseData?.child?.ruleSet?.rules?.[0]?.excludedProductIds || []
      } else {
        includedIds = salesTargetData.tabWiseData?.parent?.ruleSet?.rules?.[0]?.includedProductIds || []
        excludedIds = salesTargetData.tabWiseData?.parent?.ruleSet?.rules?.[0]?.excludedProductIds || []
      }
    }
    return [...includedIds, ...excludedIds]
  }
  const productIds = extractProductIds();
  const { setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr } = useDataTable()

  // Build productIdFilter if productIds are present
  const productIdFilter = useMemo(() => {
    if (productIds && productIds.length > 0) {
      return {
        field: 'productId',
        type: 'string',
        value: productIds.join('|')
      }
    }
    return null;
  }, [productIds])

  // Insert productIdFilter into searchArr if present
  const mergedSearchArr = useMemo(() => {
    if (productIdFilter) {
      // Remove any previous productId filter to avoid duplicates
      const filtered = searchArr.filter(f => f.field !== 'productId')
      return [...filtered, productIdFilter]
    }
    return searchArr
  }, [searchArr, productIdFilter])

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Add sorting parameters if available
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    }

    // Add pagination parameters
    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])


  const [{ data, isLoading, isFetching }] = useQueries({
    queries: [
      {
        queryKey: ['PRODUCT_LIST', queryParams, mergedSearchArr],
        queryFn: () => getProductList(queryParams, mergedSearchArr),
      },
    ],
  })

  const onDownloadBtnClick = async selectedIds => {

    const body = {
      "productIds": selectedIds,
      "filters": mergedSearchArr,
    }

    const data = await getProductCsvFile(body, queryParams)
    const fileName = `Product_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }
  const productListData = useMemo(() => GetFormattedProductData(data), [data])

  return {
    productListData,
    productListColConfigData: ProductListColConfig,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    pageIndex,
    isLoading: isLoading || isFetching,
  }
}